import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';

interface StorageImageProps {
  bucket: string;
  storedValue: string | null;
  alt: string;
  className?: string;
  onLoadError?: () => void;
}

/**
 * Extract file path from a stored URL or path.
 * Handles both legacy signed URLs and new file paths.
 */
const extractFilePath = (storedValue: string | null): string | null => {
  if (!storedValue) return null;

  // If it's already a simple path (new format), return as-is
  if (!storedValue.startsWith('http')) {
    return storedValue;
  }

  // Extract path from legacy signed URLs
  try {
    const url = new URL(storedValue);
    const pathMatch = url.pathname.match(/\/storage\/v1\/object\/sign\/[^/]+\/(.+)/);
    if (pathMatch) {
      return decodeURIComponent(pathMatch[1]);
    }
  } catch (e) {
    console.error('Failed to parse URL:', e);
  }

  return null;
};

/**
 * Component that displays an image from Supabase storage with on-demand signed URL generation.
 * Returns null if the image fails to load (no error placeholder shown).
 */
export const StorageImage = ({ bucket, storedValue, alt, className, onLoadError }: StorageImageProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const generateSignedUrl = async () => {
      if (!storedValue) {
        setLoading(false);
        setError(true);
        onLoadError?.();
        return;
      }

      const filePath = extractFilePath(storedValue);
      if (!filePath) {
        setError(true);
        setLoading(false);
        onLoadError?.();
        return;
      }

      try {
        const { data, error: signedUrlError } = await supabase.storage
          .from(bucket)
          .createSignedUrl(filePath, 3600);

        if (signedUrlError) {
          throw signedUrlError;
        }

        setSignedUrl(data.signedUrl);
      } catch (err: any) {
        console.error('Error generating signed URL:', err);
        setError(true);
        onLoadError?.();
      } finally {
        setLoading(false);
      }
    };

    generateSignedUrl();
  }, [bucket, storedValue]);

  if (loading) {
    return <Skeleton className={className || "w-full h-48"} />;
  }

  if (error || !signedUrl) {
    return null;
  }

  return (
    <img
      src={signedUrl}
      alt={alt}
      className={className}
      onError={() => {
        setError(true);
        onLoadError?.();
      }}
    />
  );
};
